import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaBullseye, FaEdit, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import Navigation from './Navigation';
import useAuth from '../../hooks/useAuth';
import AdminPageHeader from './AdminPageHeader';

function Missions() {
  const [missions, setMissions] = useState([]);
  const [filteredMissions, setFilteredMissions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { verifyAndRefreshToken, error } = useAuth();

  const fetchMissions = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('sessionToken');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/get-missions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (json.error === "0") {
        setMissions(json.missions);
        setFilteredMissions(json.missions);
      } else {
        Swal.fire('Error', 'Failed to fetch missions', 'error');
      }
    } catch (err) {
      //console.error('Fetch mission error:', err);
      Swal.fire('Error', 'Failed to fetch missions', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('sessionToken');
      if (!token) return navigate('/loginadmin');

      const isValid = await verifyAndRefreshToken(token);
      if (isValid) fetchMissions();
    };

    init();
  }, [navigate, verifyAndRefreshToken, fetchMissions]);

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);
    setFilteredMissions(
      missions.filter(m => m.title.toLowerCase().includes(value.toLowerCase()))
    );
  }, [missions]);

  const handleDelete = useCallback(async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete Mission?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      confirmButtonColor: '#d33',
    });

    if (confirm.isConfirmed) {
      const token = localStorage.getItem('sessionToken');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/delete-mission`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ id }),
        });

        const json = await res.json();
        if (json.error === "0") {
          Swal.fire('Deleted!', 'Mission has been deleted.', 'success');
          // Refresh data
          fetchMissions();
        } else {
          Swal.fire('Error', json.message || 'Failed to delete mission', 'error');
        }
      } catch (err) {
        //console.error('Delete error:', err);
        Swal.fire('Error', 'Failed to delete mission', 'error');
      }
    }
  }, [fetchMissions]);

  const columns = [
    { name: 'ID', selector: row => row.id, sortable: true },
    { name: 'Title', selector: row => row.title, sortable: true },
    { name: 'Type', selector: row => row.type },
    { name: 'Point', selector: row => row.point },
    { name: 'Duration (days)', selector: row => row.duration_days },
    { name: 'Outlet ID', selector: row => row.outlet_id },
    { name: 'Repeatable', selector: row => row.repeatable ? 'Yes' : 'No' },
    {
      name: 'Status',
      selector: row => row.is_active ? 'Active' : 'Inactive',
      cell: row => (
        <span className={`badge ${row.is_active ? 'bg-success' : 'bg-secondary'}`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      name: 'Image',
      cell: row => (
        <img
          src={`${process.env.REACT_APP_API_BASE_URL}uploads/${row.image_url}`}
          alt="mission"
          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
        />
      )
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-2">
          <Button
            variant="warning"
            size="sm"
            onClick={() => navigate(`/editmission/${row.id}`)}
          >
            <FaEdit />
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash />
          </Button>
        </div>
      )
    },
  ];

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaBullseye}
          eyebrow="Engagement program"
          title="Mission Administration"
          description="Kelola misi member secara terstruktur untuk mendukung program loyalitas dan aktivitas operasional yang terukur."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Mission Administration' },
          ]}
          actions={(
            <Button variant="primary" onClick={() => navigate('/addmission')}>
              <FaPlus /> Create Mission
            </Button>
          )}
          meta={(
            <span className="soft-stat">
              {filteredMissions.length} missions
            </span>
          )}
        />

        <div className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">{filteredMissions.length} missions</span>
            <div className="control-with-icon search-control">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by mission title"
                value={search}
                onChange={handleSearch}
              />
            </div>
          </div>

          <div className="card-body pt-0">
            <AdminDataTable
              columns={columns}
              data={filteredMissions || []}
              pagination
              progressPending={loading}
              highlightOnHover
              pointerOnHover
              striped
              responsive
              emptyTitle="No missions found"
              emptyDescription="Tambahkan misi baru untuk memulai program keterlibatan member."
            />
          </div>
        </div>
      </Container>
    </div>
  );
}

export default Missions;
